import {
  DownloadOutlined,
  EmailOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import FieldRow from "../../../components/mobile/FieldRow";
import MobileCard from "../../../components/mobile/MobileCard";
import { COLOR_CONSTANTS } from "../../../helpers/Colors";
import { convertToINR } from "../../../helpers/Utils";
import { FinsStatus, OrderStatus, POStatus } from "../constants/statusMapping";
import { getEffectivePoStatus, getPoDestination } from "../utils/poRouting";
import CustomTooltip from "./CustomTooltip";
import OrderStatusBadge from "./OrderStatusBadge";

const stopPropagation = (e) => e.stopPropagation();

export default function OrderCard({
  row,
  actionHandler,
  isActionLoading,
  setBulkSelected,
  bulkSelected,
  showSelection,
  isAllOrders,
  activeFilterStatuses,
}) {
  const navigate = useNavigate();
  const effectivePoStatus = getEffectivePoStatus(row, activeFilterStatuses);
  const isSelected = bulkSelected.some(
    (item) => item.id === row.id && item.po_code === row.po_code,
  );

  const destination = getPoDestination(row, effectivePoStatus);

  const selectRow = () => setBulkSelected((prev) => [...prev, row]);
  const deselectRow = () =>
    setBulkSelected((prev) =>
      prev.filter((item) => !(item.po_code === row.po_code)),
    );

  const handleCardClick = showSelection
    ? () => (isSelected ? deselectRow() : selectRow())
    : () =>
        navigate(destination, {
          state: { from: "dashboard", poId: row.metadata?.po_id ?? row.po_id },
        });

  const statusEntry = isAllOrders
    ? POStatus[effectivePoStatus]
    : showSelection
      ? FinsStatus[row.fins_status]
      : OrderStatus[row.order_status];

  return (
    <MobileCard onClick={handleCardClick} sx={{ pb: 0.5 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        {showSelection ? (
          <Checkbox
            size="small"
            checked={isSelected}
            onClick={stopPropagation}
            onKeyDown={stopPropagation}
            onChange={(e) => (e.target.checked ? selectRow() : deselectRow())}
            sx={{ p: 0.25, ml: -0.5 }}
          />
        ) : null}
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 700,
            color: COLOR_CONSTANTS["on-primary/variant"],
            overflowWrap: "anywhere",
          }}
        >
          {row.po_code}
        </Typography>
        {isAllOrders && !statusEntry ? null : (
          <OrderStatusBadge type={statusEntry?.code} label={statusEntry?.label} />
        )}
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        <Stack
          direction="row"
          flexWrap="wrap"
          columnGap={1.5}
          rowGap={0.25}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <FieldRow inline label="Order Value" value={convertToINR(row.order_value, 2)} />
          <FieldRow
            inline
            label="Total Invoice"
            value={convertToINR(row.total_invoice_value || 0)}
          />
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ minHeight: 40 }}
          onClick={stopPropagation}
          onKeyDown={stopPropagation}
        >
          {isActionLoading[row.po_code] ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <CustomTooltip title="View PO" arrow>
                <IconButton
                  sx={{ width: 40, height: 40 }}
                  onClick={() => actionHandler(row, "VIEW")}
                >
                  <VisibilityOutlined />
                </IconButton>
              </CustomTooltip>
              <CustomTooltip title="Download PO" arrow>
                <IconButton
                  sx={{ width: 40, height: 40 }}
                  onClick={() => actionHandler(row, "DOWNLOAD")}
                >
                  <DownloadOutlined />
                </IconButton>
              </CustomTooltip>
              {showSelection ? (
                <CustomTooltip title="Resend mail to vendor" arrow>
                  <IconButton
                    sx={{ width: 40, height: 40 }}
                    onClick={() => actionHandler(row, "MAIL")}
                  >
                    <EmailOutlined />
                  </IconButton>
                </CustomTooltip>
              ) : null}
            </>
          )}
        </Stack>
      </Stack>
    </MobileCard>
  );
}
